import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'
import {MenuTestPage} from '../pages/MenuTestPage'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import BuilderPageWrapper from '../pages/layout-builder/BuilderPageWrapper'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import ExpensePurchaseOrderAssetPage from '../modules/apps/expense-purchase-asset-order/Page'

const PrivateRoutes = () => {
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
  const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
  const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))
  const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'))
  const PatientPage = lazy(() => import('../modules/apps/patient/PatientPage'))
  const DoctorSchedulePage = lazy(
    () => import('../modules/apps/doctor-schedule/DoctorSchedulePage')
  )
  const PatientSchedulePage = lazy(
    () => import('../modules/apps/patient-schedule/PatientSchedulePage')
  )
  const SettingPage = lazy(() => import('../modules/apps/setting/SettingPage'))
  const AppointmentPage = lazy(() => import('../modules/apps/appointment/AppointmentPage'))
  const TreatmentPage = lazy(() => import('../modules/apps/treatment/Page'))
  const CrmPage = lazy(() => import('../modules/apps/crm/Page'))
  const ProductSalesPage = lazy(() => import('../modules/apps/product-sales/Page'))
  const InventoryPage = lazy(() => import('../modules/apps/inventory/inventory-routes'))
  const AssetPage = lazy(() => import('../modules/apps/asset-management/AssetPage'))
  const LabPage = lazy(() => import('../modules/apps/lab/PatientPage'))
  const FormPage = lazy(() => import('../modules/apps/form/SettingPage'))
  const AccountingSettingPage = lazy(() => import('../modules/apps/accounting/SettingPage'))
  const AccountantPage = lazy(() => import('../modules/accountant/AccountantPage'))
  const ReportPage = lazy(() => import('../modules/apps/report/SettingPage'))
  const HandbookPage = lazy(() => import('../modules/apps/handbook/SettingPage'))
  const QuotationPage = lazy(() => import('../modules/apps/quotation/Page'))
  const DepositReceiptPage = lazy(() => import('../modules/apps/deposit-receipt/Page'))
  const InvoicePage = lazy(() => import('../modules/apps/invoice/Page'))
  const RevenueReceiptPage = lazy(() => import('../modules/apps/revenue-receipt/Page'))
  const RevenueTaxInvoicePage = lazy(() => import('../modules/apps/revenue-tax-invoice/Page'))
  const RevenueCreditNotePage = lazy(() => import('../modules/apps/revenue-credit-note/Page'))
  const RevenueDebitNotePage = lazy(() => import('../modules/apps/revenue-debit-note/Page'))
  const RevenueBillingNotePage = lazy(() => import('../modules/apps/revenue-billing-note/Page'))
  const RevenueDashboardPage = lazy(() => import('../pages/DashboardRevenuePage'))
  const ExpenseRequestPurchaseOrderPage = lazy(
    () => import('../modules/apps/expense-request-purchase-order/Page')
  )
  const ExpensePurchaseOrderPage = lazy(() => import('../modules/apps/expense-purchase-order/Page'))
  const ExpensePurchaseDepositPaymentPage = lazy(
    () => import('../modules/apps/expense-deposit-payment/Page')
  )
  // ================================================
  const ExpenseDashboardPage = lazy(
    () => import('../modules/apps/expense-dashboard/view/ExpenseDashboardPage')
  )
  const ExpensePurchaseInventoryRecordPage = lazy(
    () => import('../modules/apps/expense-purchase-inventory-record/Page')
  )
  const ExpenseExpenseRecordPage = lazy(() => import('../modules/apps/expense-expense-record/Page'))
  const ExpensePurchaseTaxInvoicePage = lazy(
    () => import('../modules/apps/expense-purchase-tax-invoice/Page')
  )
  const ExpensePurchaseCreditNotePage = lazy(
    () => import('../modules/apps/expense-purchase-credit-note/Page')
  )
  const ExpensePurchaseDebitNotePage = lazy(
    () => import('../modules/apps/expense-purchase-debit-note/Page')
  )
  const ExpenseCombinedPaymentNotePage = lazy(
    () => import('../modules/apps/expense-combined-payment-note/Page')
  )
  const FinancePage = lazy(() => import('../modules/apps/finance/Page'))
  const ImportDocumentPage = lazy(() => import('../modules/import-document/ImportDocumentPage'))
  const FollowUpPage = lazy(() => import('../modules/apps/follow-up/FollowUpPage'))
  const {currentUser} = useAuth()

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route
          path='auth/*'
          element={<Navigate to={currentUser?.roomId ? '/schedule' : '/dashboard'} />}
        />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardWrapper />} />
        <Route path='builder' element={<BuilderPageWrapper />} />
        <Route path='menu-test' element={<MenuTestPage />} />
        {/* Lazy Modules */}
        <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/pages/wizards/*'
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/widgets/*'
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/chat/*'
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/user-management/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        />
        <Route
          path='patient/*'
          element={
            <SuspensedView>
              <PatientPage />
            </SuspensedView>
          }
        />
        <Route
          path='doctor-schedule/*'
          element={
            <SuspensedView>
              <DoctorSchedulePage />
            </SuspensedView>
          }
        />
        <Route
          path='schedule/*'
          element={
            <SuspensedView>
              <PatientSchedulePage />
            </SuspensedView>
          }
        />
        <Route
          path='setting/*'
          element={
            <SuspensedView>
              <SettingPage />
            </SuspensedView>
          }
        />
        <Route
          path='appointment/*'
          element={
            <SuspensedView>
              <AppointmentPage />
            </SuspensedView>
          }
        />
        <Route
          path='treatment/*'
          element={
            <SuspensedView>
              <TreatmentPage />
            </SuspensedView>
          }
        />
        <Route
          path='crm/*'
          element={
            <SuspensedView>
              <CrmPage />
            </SuspensedView>
          }
        />
         <Route
          path='follow-up/*'
          element={
            <SuspensedView>
              <FollowUpPage />
            </SuspensedView>
          }
        />
        <Route
          path='product-sales/*'
          element={
            <SuspensedView>
              <ProductSalesPage />
            </SuspensedView>
          }
        />
        
        <Route
          path='inventory/*'
          element={
            <SuspensedView>
              <InventoryPage />
            </SuspensedView>
          }
        />

        <Route
          path='asset-management/*'
          element={
            <SuspensedView>
              <AssetPage />
            </SuspensedView>
          }
        />

        <Route
          path='lab/*'
          element={
            <SuspensedView>
              <LabPage />
            </SuspensedView>
          }
        />

        <Route
          path='form/*'
          element={
            <SuspensedView>
              <FormPage />
            </SuspensedView>
          }
        />

        <Route
          path='accounting/*'
          element={
            <SuspensedView>
              <AccountantPage />
            </SuspensedView>
          }
        />

        <Route
          path='accounting-settings/*'
          element={
            <SuspensedView>
              <AccountingSettingPage />
            </SuspensedView>
          }
        />

        <Route
          path='report/*'
          element={
            <SuspensedView>
              <ReportPage />
            </SuspensedView>
          }
        />

        <Route
          path='import-document'
          element={
            <SuspensedView>
              <ImportDocumentPage />
            </SuspensedView>
          }
        />

        <Route
          path='handbook/*'
          element={
            <SuspensedView>
              <HandbookPage />
            </SuspensedView>
          }
        />
        <Route
          path='quotation/*'
          element={
            <SuspensedView>
              <QuotationPage />
            </SuspensedView>
          }
        />
        <Route
          path='deposit-receipt/*'
          element={
            <SuspensedView>
              <DepositReceiptPage />
            </SuspensedView>
          }
        />
        <Route
          path='invoice/*'
          element={
            <SuspensedView>
              <InvoicePage />
            </SuspensedView>
          }
        />
        <Route
          path='revenue-dashboard/*'
          element={
            <SuspensedView>
              <RevenueDashboardPage />
            </SuspensedView>
          }
        />
        <Route
          path='revenue-receipt/*'
          element={
            <SuspensedView>
              <RevenueReceiptPage />
            </SuspensedView>
          }
        />
        <Route
          path='revenue-tax-invoice/*'
          element={
            <SuspensedView>
              <RevenueTaxInvoicePage />
            </SuspensedView>
          }
        />
        <Route
          path='revenue-credit-note/*'
          element={
            <SuspensedView>
              <RevenueCreditNotePage />
            </SuspensedView>
          }
        />
        <Route
          path='revenue-debit-note/*'
          element={
            <SuspensedView>
              <RevenueDebitNotePage />
            </SuspensedView>
          }
        />
        <Route
          path='revenue-billing-note/*'
          element={
            <SuspensedView>
              <RevenueBillingNotePage />
            </SuspensedView>
          }
        />
        <Route
          path='expense-request-purchase-order/*'
          element={
            <SuspensedView>
              <ExpenseRequestPurchaseOrderPage />
            </SuspensedView>
          }
        />
        <Route
          path='expense-purchase-order/*'
          element={
            <SuspensedView>
              <ExpensePurchaseOrderPage />
            </SuspensedView>
          }
        />
        <Route
          path='expense-purchase-asset-order/*'
          element={
            <SuspensedView>
              <ExpensePurchaseOrderAssetPage />
            </SuspensedView>
          }
        />
        <Route
          path='expense-deposit-payment/*'
          element={
            <SuspensedView>
              <ExpensePurchaseDepositPaymentPage />
            </SuspensedView>
          }
        />
        {/*<Route*/}
        {/*  path='expense-deposit-payment/*'*/}
        {/*  element={*/}
        {/*    <SuspensedView>*/}
        {/*      <ExpensePurchaseDepositPaymentPage />*/}
        {/*    </SuspensedView>*/}
        {/*  }*/}
        {/*/>*/}
        <Route
          path='expense-dashboard/*'
          element={
            <SuspensedView>
              <ExpenseDashboardPage />
            </SuspensedView>
          }
        />
        <Route
          path='expense-purchase-inventory-record/*'
          element={
            <SuspensedView>
              <ExpensePurchaseInventoryRecordPage />
            </SuspensedView>
          }
        />
        <Route
          path='expense-expense-record/*'
          element={
            <SuspensedView>
              <ExpenseExpenseRecordPage />
            </SuspensedView>
          }
        />
        <Route
          path='expense-purchase-tax-invoice/*'
          element={
            <SuspensedView>
              <ExpensePurchaseTaxInvoicePage />
            </SuspensedView>
          }
        />
        <Route
          path='expense-purchase-credit-note/*'
          element={
            <SuspensedView>
              <ExpensePurchaseCreditNotePage />
            </SuspensedView>
          }
        />
        <Route
          path='expense-purchase-debit-note/*'
          element={
            <SuspensedView>
              <ExpensePurchaseDebitNotePage />
            </SuspensedView>
          }
        />
        <Route
          path='expense-combined-payment-note/*'
          element={
            <SuspensedView>
              <ExpenseCombinedPaymentNotePage />
            </SuspensedView>
          }
        />
        <Route
          path='finance/*'
          element={
            <SuspensedView>
              <FinancePage />
            </SuspensedView>
          }
        />
        <Route
          path='accountant/*'
          element={
            <SuspensedView>
              <AccountantPage />
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
